import { Injectable } from '@angular/core';
import { generateClient } from 'aws-amplify/data';
import { uploadData, downloadData, remove, list } from 'aws-amplify/storage';
import { Observable, from, throwError, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import type { Schema } from '../../../amplify/data/resource';
import { StorageService } from './storage.service';

type Activity = Schema['Activity']['type'];
type Availability = Schema['Availability']['type'];
type Company = Schema['Company']['type'];
type PotentialCompanyInfo = Schema['PotentialCompanyInfo']['type'];
type Booking = Schema['Booking']['type'];
type Users = Schema['Users']['type'];
type Store = Schema['Store']['type'];
type Picture = Schema['Picture']['type'];
type HotelPartner = Schema['HotelPartner']['type'];
type BookingReferral = Schema['BookingReferral']['type'];
type SiteConfigurationSchema = Schema['SiteConfiguration']['type'];

@Injectable({ providedIn: 'root' })
export class ServiceService {
  private client = generateClient<Schema>();
  private readonly publicReadOptions = { authMode: 'identityPool' as const };
  private readonly guestCreateOptions = { authMode: 'identityPool' as const };

  constructor(private storageService: StorageService) {}

  // ==================== ACTIVITY METHODS ====================

  /**
   * Get all activities (excluding archived)
   */
  getActivities(): Observable<Activity[]> {
    return from(this.client.models.Activity.list(this.publicReadOptions)).pipe(
      map(response => response.data?.filter(activity => !activity.archived) ?? []),
      catchError(error => throwError(() => new Error(`Failed to fetch activities: ${error.message}`)))
    );
  }

  /**
   * Get all archived activities
   */
  getArchivedActivities(): Observable<Activity[]> {
    return from(this.client.models.Activity.list(this.publicReadOptions)).pipe(
      map(response => response.data?.filter(activity => activity.archived === true) ?? []),
      catchError(error => throwError(() => new Error(`Failed to fetch archived activities: ${error.message}`)))
    );
  }

  /**
   * Get activity by ID
   */
  getActivityById(id: string): Observable<Activity | null> {
    return from(this.client.models.Activity.get({ id }, this.publicReadOptions)).pipe(
      map(response => response.data),
      catchError(error => throwError(() => new Error(`Failed to fetch activity: ${error.message}`)))
    );
  }

  /**
   * Get activities by main activity type (excluding archived)
   */
  getActivitiesByType(mainActivityType: string): Observable<Activity[]> {
    return from(this.client.models.Activity.list({
      filter: { mainActivityType: { eq: mainActivityType } },
      ...this.publicReadOptions,
    })).pipe(
      map(response => response.data?.filter(activity => !activity.archived) ?? []),
      catchError(error => throwError(() => new Error(`Failed to fetch activities by type: ${error.message}`)))
    );
  }

  /**
   * Get top pick activities (excluding archived)
   */
  getTopPickActivities(): Observable<Activity[]> {
    // The filter value for a boolean field should be a boolean, not a string
    return from(this.client.models.Activity.list(this.publicReadOptions)).pipe(
      map(response => response.data?.filter(activity => activity.topPick === true && !activity.archived) ?? []),
      catchError(error => throwError(() => new Error(`Failed to fetch top pick activities: ${error?.message ?? error}`)))
    );
  }
  
  

  /**
   * Create new activity (default archived to false)
   */
  createActivity(activityData: Partial<Activity>): Observable<Activity> {
    const dataWithDefaults = { ...activityData, archived: activityData.archived ?? false };
    return from(this.client.models.Activity.create(dataWithDefaults as any)).pipe(
      map(response => response.data),
      catchError(error => throwError(() => new Error(`Failed to create activity: ${error.message}`)))
    );
  }

  /**
   * Update activity
   */
  updateActivity(id: string, activityData: Partial<Activity>): Observable<Activity> {
    return from(this.client.models.Activity.update({ id, ...activityData } as any)).pipe(
      map(response => response.data),
      catchError(error => throwError(() => new Error(`Failed to update activity: ${error.message}`)))
    );
  }

  /**
   * Delete activity and all related data including images
   */
  deleteActivity(id: string): Observable<Activity> {
    return from(this.deleteActivityWithImages(id).then(() => ({ id } as Activity))).pipe(
      catchError(error => {
        console.error('[DELETE] Error in deleteActivity observable:', error);
        return throwError(() => new Error(`Failed to delete activity: ${error.message || 'Unknown error'}`));
      })
    );
  }

  /**
   * Delete activity with all related data including storage images
   */
  private async deleteActivityWithImages(activityId: string): Promise<void> {
    try {
      const pictures = await this.client.models.Picture.list({
        filter: { activityID: { eq: activityId } }
      });

      for (const picture of pictures.data) {
        if (picture.url) {
          try {
            await remove({ key: picture.url, options: { accessLevel: 'guest' } });
          } catch (storageError) {
            console.warn(`Failed to delete storage file ${picture.url}:`, storageError);
          }
        }
      }

      await this.deleteActivityRelatedData(activityId);
      const deleteResult = await this.client.models.Activity.delete({ id: activityId });
      if (!deleteResult.data) {
        throw new Error('Activity deletion returned no data');
      }
    } catch (error) {
      console.error(`Error deleting activity ${activityId}:`, error);
      throw error;
    }
  }

  // ==================== TIME SLOTS METHODS ====================

  // TimeSlots model has been removed - time slots are now stored as JSON in Availability table

  // ==================== AVAILABILITY METHODS ====================

  /**
   * Get all availabilities
   */
  getAvailabilities(): Observable<Availability[]> {
    return from(this.client.models.Availability.list(this.publicReadOptions)).pipe(
      map(response => response.data),
      catchError(error => throwError(() => new Error(`Failed to fetch availabilities: ${error.message}`)))
    );
  }

  /**
   * Get availability by activity ID
   */
  getAvailabilityByActivityId(activityID: string): Observable<Availability[]> {
    return from(this.client.models.Availability.list({
      filter: { activityID: { eq: activityID } },
      ...this.publicReadOptions,
    })).pipe(
      map(response => response.data),
      catchError(error => throwError(() => new Error(`Failed to fetch availability by activity: ${error.message}`)))
    );
  }

  /**
   * Get availability by date
   */
  getAvailabilityByDate(date: string): Observable<Availability[]> {
    return from(this.client.models.Availability.list({
      filter: { date: { eq: date } },
      ...this.publicReadOptions,
    })).pipe(
      map(response => response.data),
      catchError(error => throwError(() => new Error(`Failed to fetch availability by date: ${error.message}`)))
    );
  }

  /**
   * Create new availability
   */
  createAvailability(availabilityData: Partial<Availability>): Observable<Availability> {
    return from(this.client.models.Availability.create(availabilityData)).pipe(
      map(response => response.data),
      catchError(error => throwError(() => new Error(`Failed to create availability: ${error.message}`)))
    );
  }

  /**
   * Update availability
   */
  updateAvailability(id: string, availabilityData: Partial<Availability>): Observable<Availability> {
    return from(this.client.models.Availability.update({ id, ...availabilityData } as any)).pipe(
      map(response => response.data),
      catchError(error => throwError(() => new Error(`Failed to update availability: ${error.message}`)))
    );
  }

  /**
   * Delete availability
   */
  deleteAvailability(id: string): Observable<Availability> {
    return from(this.client.models.Availability.delete({ id })).pipe(
      map(response => response.data),
      catchError(error => throwError(() => new Error(`Failed to delete availability: ${error.message}`)))
    );
  }

  // ==================== STORE METHODS ====================

  /**
   * Get all stores
   */
  getStores(): Observable<Store[]> {
    return from(this.client.models.Store.list(this.publicReadOptions)).pipe(
      map(response => response.data),
      catchError(error => throwError(() => new Error(`Failed to fetch stores: ${error.message}`)))
    );
  }

  /**
   * Get stores by site location (for multi-domain filtering)
   */
  getStoresByLocation(siteLocation: string): Observable<Store[]> {
    return from(this.client.models.Store.list({
      filter: { siteLocation: { eq: siteLocation } },
      ...this.publicReadOptions,
    })).pipe(
      map(response => response.data ?? []),
      catchError(error => throwError(() => new Error(`Failed to fetch stores by location: ${error.message}`)))
    );
  }

  /**
   * Get store by ID
   */
  getStoreById(id: string): Observable<Store | null> {
    return from(this.client.models.Store.get({ id }, this.publicReadOptions)).pipe(
      map(response => response.data),
      catchError(error => throwError(() => new Error(`Failed to fetch store: ${error.message}`)))
    );
  }

  /**
   * Get stores by company ID (if there's a company relationship)
   */
  getStoresByCompanyId(companyId: string): Observable<Store[]> {
    return from(this.client.models.Store.list({
      filter: { companyID: { eq: companyId } }
    })).pipe(
      map(response => response.data),
      catchError(error => throwError(() => new Error(`Failed to fetch stores by company: ${error.message}`)))
    );
  }

  /**
   * Create new store
   */
  createStore(storeData: Partial<Store>): Observable<Store> {
    return from(this.client.models.Store.create(storeData)).pipe(
      map(response => response.data),
      catchError(error => throwError(() => new Error(`Failed to create store: ${error.message}`)))
    );
  }

  /**
   * Update store
   */
  updateStore(id: string, storeData: Partial<Store>): Observable<Store> {
    return from(this.client.models.Store.update({ id, ...storeData } as any)).pipe(
      map(response => response.data),
      catchError(error => throwError(() => new Error(`Failed to update store: ${error.message}`)))
    );
  }

  /**
   * Delete store
   */
  deleteStore(id: string): Observable<Store> {
    return from(this.client.models.Store.delete({ id })).pipe(
      map(response => response.data),
      catchError(error => throwError(() => new Error(`Failed to delete store: ${error.message}`)))
    );
  }

  /**
   * Get activities by site location (activities from stores in the given location, excluding archived)
   */
  getActivitiesByLocation(siteLocation: string): Observable<Activity[]> {
    return this.getStoresByLocation(siteLocation).pipe(
      switchMap(stores => {
        const storeIds = new Set(stores.map(s => s.id).filter((id): id is string => !!id));
        if (storeIds.size === 0) {
          return of([]);
        }
        return from(this.client.models.Activity.list(this.publicReadOptions)).pipe(
          map(response => {
            const activities = response.data ?? [];
            return activities.filter(
              a => !a.archived && a.storeID && storeIds.has(a.storeID)
            );
          })
        );
      }),
      catchError(error => throwError(() => new Error(`Failed to fetch activities by location: ${error.message}`)))
    );
  }

  /**
   * Get activities by store ID (excluding archived)
   */
  getActivitiesByStoreId(storeId: string): Observable<Activity[]> {
    return from(this.client.models.Activity.list({
      filter: { storeID: { eq: storeId } },
      ...this.publicReadOptions,
    })).pipe(
      map(response => response.data?.filter(activity => !activity.archived) ?? []),
      catchError(error => throwError(() => new Error(`Failed to fetch activities by store: ${error.message}`)))
    );
  }

  /**
   * Get archived activities by store ID
   */
  getArchivedActivitiesByStoreId(storeId: string): Observable<Activity[]> {
    return from(this.client.models.Activity.list({
      filter: { storeID: { eq: storeId } },
      ...this.publicReadOptions,
    })).pipe(
      map(response => response.data?.filter(activity => activity.archived === true) ?? []),
      catchError(error => throwError(() => new Error(`Failed to fetch archived activities by store: ${error.message}`)))
    );
  }

  /**
   * Archive an activity
   */
  archiveActivity(id: string): Observable<Activity> {
    return from(this.client.models.Activity.update({ id, archived: true } as any)).pipe(
      map(response => response.data),
      catchError(error => throwError(() => new Error(`Failed to archive activity: ${error.message}`)))
    );
  }

  /**
   * Unarchive an activity
   */
  unarchiveActivity(id: string): Observable<Activity> {
    return from(this.client.models.Activity.update({ id, archived: false } as any)).pipe(
      map(response => response.data),
      catchError(error => throwError(() => new Error(`Failed to unarchive activity: ${error.message}`)))
    );
  }

  // ==================== PICTURE METHODS ====================

  /**
   * Get all pictures
   */
  getPictures(): Observable<Picture[]> {
    return from(this.client.models.Picture.list(this.publicReadOptions)).pipe(
      map(response => response.data),
      catchError(error => throwError(() => new Error(`Failed to fetch pictures: ${error.message}`)))
    );
  }

  /**
   * Get pictures by activity ID
   */
  getPicturesByActivityId(activityID: string): Observable<Picture[]> {
    return from(this.client.models.Picture.list({
      filter: { activityID: { eq: activityID } },
      ...this.publicReadOptions,
    })).pipe(
      map(response => response.data),
      catchError(error => throwError(() => new Error(`Failed to fetch pictures by activity: ${error.message}`)))
    );
  }

  /**
   * Create new picture
   */
  createPicture(pictureData: Partial<Picture>): Observable<Picture> {
    return from(this.client.models.Picture.create(pictureData)).pipe(
      map(response => response.data),
      catchError(error => throwError(() => new Error(`Failed to create picture: ${error.message}`)))
    );
  }

  /**
   * Update picture
   */
  updatePicture(id: string, pictureData: Partial<Picture>): Observable<Picture> {
    return from(this.client.models.Picture.update({ id, ...pictureData } as any)).pipe(
      map(response => response.data),
      catchError(error => throwError(() => new Error(`Failed to update picture: ${error.message}`)))
    );
  }

  /**
   * Delete picture
   */
  deletePicture(id: string): Observable<Picture> {
    return from(this.client.models.Picture.delete({ id })).pipe(
      map(response => response.data),
      catchError(error => throwError(() => new Error(`Failed to delete picture: ${error.message}`)))
    );
  }

  // ==================== COMPANY METHODS ====================

  /**
   * Get all companies
   */
  getCompanies(): Observable<Company[]> {
    return from(this.client.models.Company.list({})).pipe(
      map(response => response.data),
      catchError(error => throwError(() => new Error(`Failed to fetch companies: ${error.message}`)))
    );
  }

  /**
   * Get company by ID
   */
  getCompanyById(id: string): Observable<Company | null> {
    return from(this.client.models.Company.get({ id })).pipe(
      map(response => response.data),
      catchError(error => throwError(() => new Error(`Failed to fetch company: ${error.message}`)))
    );
  }

  /**
   * Get companies by status
   */
  getCompaniesByStatus(status: string): Observable<Company[]> {
    return from(this.client.models.Company.list({
      filter: { status: { eq: status } }
    })).pipe(
      map(response => response.data),
      catchError(error => throwError(() => new Error(`Failed to fetch companies by status: ${error.message}`)))
    );
  }

  /**
   * Create company
   */
  createCompany(companyData: Partial<Company>): Observable<Company> {
    return from(this.client.models.Company.create(companyData as any)).pipe(
      map(response => response.data),
      catchError(error => throwError(() => new Error(`Failed to create company: ${error.message}`)))
    );
  }

  /**
   * Update company
   */
  updateCompany(id: string, companyData: Partial<Company>): Observable<Company> {
    return from(this.client.models.Company.update({ id, ...companyData } as any)).pipe(
      map(response => response.data),
      catchError(error => throwError(() => new Error(`Failed to update company: ${error.message}`)))
    );
  }

  /**
   * Delete company
   */
  deleteCompany(id: string): Observable<Company> {
    return from(this.client.models.Company.delete({ id })).pipe(
      map(response => response.data),
      catchError(error => throwError(() => new Error(`Failed to delete company: ${error.message}`)))
    );
  }

  /**
   * Get company stores
   */
  getCompanyStores(companyId: string): Observable<Store[]> {
    return from(this.client.models.Store.list({
      filter: { companyID: { eq: companyId } }
    })).pipe(
      map(response => response.data),
      catchError(error => throwError(() => new Error(`Failed to fetch company stores: ${error.message}`)))
    );
  }

  /**
   * Get company users
   */
  getCompanyUsers(companyId: string): Observable<Users[]> {
    return from(this.client.models.Users.list({
      filter: { companyID: { eq: companyId } }
    })).pipe(
      map(response => response.data),
      catchError(error => throwError(() => new Error(`Failed to fetch company users: ${error.message}`)))
    );
  }

  // ==================== POTENTIAL COMPANY INFO METHODS ====================

  /**
   * Get all company info
   */
  getPotentialCompanyInfo(): Observable<PotentialCompanyInfo[]> {
    return from(this.client.models.PotentialCompanyInfo.list({})).pipe(
      map(response => response.data),
      catchError(error => throwError(() => new Error(`Failed to fetch company info: ${error.message}`)))
    );
  }

  /**
   * Create new company info
   */
  createPotentialCompanyInfo(companyData: Partial<PotentialCompanyInfo>): Observable<PotentialCompanyInfo> {
    return from(this.client.models.PotentialCompanyInfo.create(companyData)).pipe(
      map(response => response.data),
      catchError(error => throwError(() => new Error(`Failed to create company info: ${error.message}`)))
    );
  }

  /**
   * Update company info
   */
  updatePotentialCompanyInfo(id: string, companyData: Partial<PotentialCompanyInfo>): Observable<PotentialCompanyInfo> {
    return from(this.client.models.PotentialCompanyInfo.update({ id, ...companyData } as any)).pipe(
      map(response => response.data),
      catchError(error => throwError(() => new Error(`Failed to update company info: ${error.message}`)))
    );
  }

  /**
   * Delete company info
   */
  deletePotentialCompanyInfo(id: string): Observable<PotentialCompanyInfo> {
    return from(this.client.models.PotentialCompanyInfo.delete({ id })).pipe(
      map(response => response.data),
      catchError(error => throwError(() => new Error(`Failed to delete company info: ${error.message}`)))
    );
  }

  // ==================== STORAGE METHODS ====================

  /**
   * Upload file to public folder
   */
  uploadPublicFile(file: File, key: string): Observable<any> {
    return from(uploadData({
      key: `public/${key}`,
      data: file,
      options: {
        accessLevel: 'guest'
      }
    }).result).pipe(
      catchError(error => throwError(() => new Error(`Failed to upload public file: ${error.message}`)))
    );
  }

  /**
   * Upload activity image
   */
  uploadActivityImage(file: File, activityId: string, filename?: string): Observable<any> {
    const key = `activity-images/${activityId}/${filename || file.name}`;
    return from(uploadData({
      key,
      data: file,
      options: {
        accessLevel: 'guest'
      }
    }).result).pipe(
      catchError(error => throwError(() => new Error(`Failed to upload activity image: ${error.message}`)))
    );
  }

  /**
   * Upload user file (authenticated users only)
   */
  uploadUserFile(file: File, userId: string, filename?: string): Observable<any> {
    const key = `user-uploads/${userId}/${filename || file.name}`;
    return from(uploadData({
      key,
      data: file,
      options: {
        accessLevel: 'private'
      }
    }).result).pipe(
      catchError(error => throwError(() => new Error(`Failed to upload user file: ${error.message}`)))
    );
  }

  /**
   * Download file
   */
  downloadFile(key: string): Observable<Blob> {
    return from(downloadData({ key }).result).pipe(
      map(response => response.body as unknown as Blob),
      catchError(error => throwError(() => new Error(`Failed to download file: ${error.message}`)))
    );
  }

  /**
   * Get file URL
   */
  getFileUrl(key: string): Observable<string> {
    return from(downloadData({ key }).result).pipe(
      map(response => URL.createObjectURL(response.body as unknown as Blob)),
      catchError(error => throwError(() => new Error(`Failed to get file URL: ${error.message}`)))
    );
  }

  /**
   * List files in a directory
   */
  listFiles(prefix: string): Observable<any[]> {
    return from(list({ prefix })).pipe(
      map(response => response.items),
      catchError(error => throwError(() => new Error(`Failed to list files: ${error.message}`)))
    );
  }

  /**
   * Delete file
   */
  deleteFile(key: string): Observable<any> {
    return from(remove({ key })).pipe(
      catchError(error => throwError(() => new Error(`Failed to delete file: ${error.message}`)))
    );
  }

  /**
   * Delete activity images
   */
  deleteActivityImages(activityId: string): Observable<void> {
    return this.listFiles(`activity-images/${activityId}/`).pipe(
      switchMap(files => {
        const keys = files.map((f: any) => f.key).filter(Boolean);
        if (keys.length === 0) return of(undefined);
        return forkJoin(keys.map((key: string) => this.deleteFile(key)));
      }),
      map(() => void 0),
      catchError(error => throwError(() => new Error(`Failed to delete activity images: ${error.message}`)))
    );
  }

  // ==================== BOOKING METHODS ====================

  /**
   * Get all bookings
   */
  getBookings(): Observable<Booking[]> {
    return from(this.client.models.Booking.list({})).pipe(
      map(response => response.data),
      catchError(error => throwError(() => new Error(`Failed to fetch bookings: ${error.message}`)))
    );
  }

  /**
   * Get booking by ID
   */
  getBookingById(id: string): Observable<Booking | null> {
    return from(this.client.models.Booking.get({ id })).pipe(
      map(response => response.data),
      catchError(error => throwError(() => new Error(`Failed to fetch booking: ${error.message}`)))
    );
  }

  /**
   * Get bookings by user ID
   */
  getBookingsByUserId(userId: string): Observable<Booking[]> {
    return from(this.client.models.Booking.list({
      filter: { userID: { eq: userId } }
    })).pipe(
      map(response => response.data),
      catchError(error => throwError(() => new Error(`Failed to fetch user bookings: ${error.message}`)))
    );
  }

  /**
   * Get bookings by activity ID
   */
  getBookingsByActivityId(activityId: string): Observable<Booking[]> {
    return from(this.client.models.Booking.list({
      filter: { activityID: { eq: activityId } }
    })).pipe(
      map(response => response.data),
      catchError(error => throwError(() => new Error(`Failed to fetch activity bookings: ${error.message}`)))
    );
  }

  /**
   * Create new booking
   */
  createBooking(bookingData: Partial<Booking>): Observable<Booking> {
    return from(this.client.models.Booking.create(bookingData, this.guestCreateOptions)).pipe(
      map(response => response.data),
      catchError(error => throwError(() => new Error(`Failed to create booking: ${error.message}`)))
    );
  }

  /**
   * Update booking
   */
  updateBooking(id: string, bookingData: Partial<Booking>): Observable<Booking> {
    return from(this.client.models.Booking.update({ id, ...bookingData } as any)).pipe(
      map(response => response.data),
      catchError(error => throwError(() => new Error(`Failed to update booking: ${error.message}`)))
    );
  }

  /**
   * Delete booking
   */
  deleteBooking(id: string): Observable<Booking> {
    return from(this.client.models.Booking.delete({ id })).pipe(
      map(response => response.data),
      catchError(error => throwError(() => new Error(`Failed to delete booking: ${error.message}`)))
    );
  }

  // ==================== HOTEL PARTNER METHODS ====================

  /**
   * Get hotel partners for admin/reporting and public referral-code lookup.
   */
  getHotelPartners(): Observable<HotelPartner[]> {
    return from(this.client.models.HotelPartner.list({})).pipe(
      map(response => response.data ?? []),
      catchError(error => throwError(() => new Error(`Failed to fetch hotel partners: ${error.message}`)))
    );
  }

  getActiveHotelPartnersByLocation(siteLocation: string): Observable<HotelPartner[]> {
    return from(this.client.models.HotelPartner.list({
      filter: {
        and: [
          { siteLocation: { eq: siteLocation } },
          { active: { eq: true } }
        ]
      },
    } as any)).pipe(
      map(response => response.data ?? []),
      catchError(error => throwError(() => new Error(`Failed to fetch hotel partners by location: ${error.message}`)))
    );
  }

  getHotelPartnerByCode(code: string): Observable<HotelPartner | null> {
    const normalizedCode = code.trim().toUpperCase();
    if (!normalizedCode) {
      return of(null);
    }

    return from(this.client.models.HotelPartner.list({
      filter: { code: { eq: normalizedCode } },
    } as any)).pipe(
      map(response => (response.data ?? []).find((hotel) => hotel.active !== false) ?? null),
      catchError(error => throwError(() => new Error(`Failed to fetch hotel partner by code: ${error.message}`)))
    );
  }

  createHotelPartner(hotelData: Partial<HotelPartner>): Observable<HotelPartner> {
    const data = {
      ...hotelData,
      code: hotelData.code?.trim().toUpperCase(),
      active: hotelData.active ?? true,
    };
    return from(this.client.models.HotelPartner.create(data as any)).pipe(
      map(response => response.data as HotelPartner),
      catchError(error => throwError(() => new Error(`Failed to create hotel partner: ${error.message}`)))
    );
  }

  updateHotelPartner(id: string, hotelData: Partial<HotelPartner>): Observable<HotelPartner> {
    const data = {
      ...hotelData,
      code: hotelData.code?.trim().toUpperCase(),
    };
    return from(this.client.models.HotelPartner.update({ id, ...(data as any) })).pipe(
      map(response => response.data as HotelPartner),
      catchError(error => throwError(() => new Error(`Failed to update hotel partner: ${error.message}`)))
    );
  }

  deleteHotelPartner(id: string): Observable<HotelPartner> {
    return from(this.client.models.HotelPartner.delete({ id })).pipe(
      map(response => response.data as HotelPartner),
      catchError(error => throwError(() => new Error(`Failed to delete hotel partner: ${error.message}`)))
    );
  }

  createBookingReferral(referralData: Partial<BookingReferral>): Observable<BookingReferral> {
    return from(this.client.models.BookingReferral.create(referralData as any)).pipe(
      map(response => response.data as BookingReferral),
      catchError(error => throwError(() => new Error(`Failed to create booking referral: ${error.message}`)))
    );
  }

  getBookingReferrals(): Observable<BookingReferral[]> {
    return from(this.client.models.BookingReferral.list({})).pipe(
      map(response => response.data ?? []),
      catchError(error => throwError(() => new Error(`Failed to fetch booking referrals: ${error.message}`)))
    );
  }

  getBookingReferralsByHotelPartner(hotelPartnerId: string): Observable<BookingReferral[]> {
    return from(this.client.models.BookingReferral.list({
      filter: { hotelPartnerID: { eq: hotelPartnerId } }
    })).pipe(
      map(response => response.data ?? []),
      catchError(error => throwError(() => new Error(`Failed to fetch hotel booking referrals: ${error.message}`)))
    );
  }

  // ==================== USER METHODS ====================

  /**
   * Get all users
   */
  getUsers(): Observable<Users[]> {
    return from(this.client.models.Users.list({})).pipe(
      map(response => response.data),
      catchError(error => throwError(() => new Error(`Failed to fetch users: ${error.message}`)))
    );
  }

  /**
   * Get user by ID
   */
  getUserById(id: string): Observable<Users | null> {
    return from(this.client.models.Users.get({ id })).pipe(
      map(response => response.data),
      catchError(error => throwError(() => new Error(`Failed to fetch user: ${error.message}`)))
    );
  }

  /**
   * Get user by email
   */
  getUserByEmail(email: string): Observable<Users | null> {
    return from(this.client.models.Users.list({
      filter: { email: { eq: email } }
    })).pipe(
      map(response => response.data?.[0] || null),
      catchError(error => throwError(() => new Error(`Failed to fetch user by email: ${error.message}`)))
    );
  }

  /**
   * Create new user
   */
  createUser(userData: Partial<Users>): Observable<Users> {
    return from(this.client.models.Users.create(userData)).pipe(
      map(response => response.data),
      catchError(error => throwError(() => new Error(`Failed to create user: ${error.message}`)))
    );
  }

  /**
   * Update user
   */
  updateUser(id: string, userData: Partial<Users>): Observable<Users> {
    return from(this.client.models.Users.update({ id, ...userData } as any)).pipe(
      map(response => response.data),
      catchError(error => throwError(() => new Error(`Failed to update user: ${error.message}`)))
    );
  }

  /**
   * Delete user
   */
  deleteUser(id: string): Observable<Users> {
    return from(this.client.models.Users.delete({ id })).pipe(
      map(response => response.data),
      catchError(error => throwError(() => new Error(`Failed to delete user: ${error.message}`)))
    );
  }

  // ==================== CASCADE DELETION METHODS ====================

  /**
   * Delete company and all associated data (cascade deletion)
   * This will delete:
   * - Company info
   * - All users associated with the company
   * - All stores associated with the company
   * - All activities associated with those stores
   * - All related data (bookings, payments, favorites, etc.)
   */
  deleteCompanyCascade(companyId: string): Observable<any> {
    return from(this.performCascadeDeletion(companyId));
  }

  /**
   * Perform the actual cascade deletion
   */
  private async performCascadeDeletion(companyId: string): Promise<any> {
    const deletionResults: {
      company: string | null;
      users: string[];
      stores: string[];
      activities: string[];
      bookings: string[];
      payments: string[];
      favorites: string[];
      errors: string[];
    } = {
      company: null,
      users: [],
      stores: [],
      activities: [],
      bookings: [],
      payments: [],
      favorites: [],
      errors: []
    };

    try {
      // 1. Get company info first
      const company = await this.client.models.Company.get({ id: companyId });
      if (!company.data) {
        throw new Error('Company not found');
      }

      // 2. Get all users associated with this company
      const users = await this.client.models.Users.list({
        filter: { companyID: { eq: companyId } }
      });

      // 3. Get all stores associated with this company
      const stores = await this.client.models.Store.list({
        filter: { companyID: { eq: companyId } }
      });

      // 4. Get all activities for these stores
      const storeIds = stores.data.map(store => store.id);
      let activities: any[] = [];
      for (const storeId of storeIds) {
        const storeActivities = await this.client.models.Activity.list({
          filter: { storeID: { eq: storeId } }
        });
        activities = activities.concat(storeActivities.data);
      }

      // 5. Get all bookings for these activities
      const activityIds = activities.map(activity => activity.id);
      let bookings: any[] = [];
      for (const activityId of activityIds) {
        const activityBookings = await this.client.models.Booking.list({
          filter: { activityID: { eq: activityId } }
        });
        bookings = bookings.concat(activityBookings.data);
      }

      // 6. Get all payments for these bookings
      const bookingIds = bookings.map(booking => booking.id);
      let payments: any[] = [];
      for (const bookingId of bookingIds) {
        const bookingPayments = await this.client.models.Payments.list({
          filter: { bookingID: { eq: bookingId } }
        });
        payments = payments.concat(bookingPayments.data);
      }

      // 7. Get all favorites for these activities
      let favorites: any[] = [];
      for (const activityId of activityIds) {
        const activityFavorites = await this.client.models.Favorites.list({
          filter: { activityID: { eq: activityId } }
        });
        favorites = favorites.concat(activityFavorites.data);
      }

      // 8. Start cascade deletion

      // Delete payments first (they reference bookings)
      for (const payment of payments) {
        try {
          await this.client.models.Payments.delete({ id: payment.id });
          deletionResults.payments.push(payment.id);
        } catch (error) {
          deletionResults.errors.push(`Failed to delete payment ${payment.id}: ${error}`);
        }
      }

      // Delete bookings
      for (const booking of bookings) {
        try {
          await this.client.models.Booking.delete({ id: booking.id });
          deletionResults.bookings.push(booking.id);
        } catch (error) {
          deletionResults.errors.push(`Failed to delete booking ${booking.id}: ${error}`);
        }
      }

      // Delete favorites
      for (const favorite of favorites) {
        try {
          await this.client.models.Favorites.delete({ id: favorite.id });
          deletionResults.favorites.push(favorite.id);
        } catch (error) {
          deletionResults.errors.push(`Failed to delete favorite ${favorite.id}: ${error}`);
        }
      }

      // Delete activities and their related data
      for (const activity of activities) {
        try {
          // Delete activity-related data first
          await this.deleteActivityRelatedData(activity.id);
          
          // Delete the activity
          await this.client.models.Activity.delete({ id: activity.id });
          deletionResults.activities.push(activity.id);
        } catch (error) {
          deletionResults.errors.push(`Failed to delete activity ${activity.id}: ${error}`);
        }
      }

      // Delete stores
      for (const store of stores.data) {
        try {
          await this.client.models.Store.delete({ id: store.id });
          deletionResults.stores.push(store.id);
        } catch (error) {
          deletionResults.errors.push(`Failed to delete store ${store.id}: ${error}`);
        }
      }

      // Delete users
      for (const user of users.data) {
        try {
          // Delete from database first
          await this.client.models.Users.delete({ id: user.id });
          deletionResults.users.push(user.id);
          
          // Delete from Cognito (if AuthService is available)
          try {
            // Note: This would require injecting AuthService, but for now we'll log it
            console.log(`User ${user.email} should be deleted from Cognito`);
            // In a real implementation, you would call:
            // await this.authService.deleteUserFromCognito(user.email);
          } catch (cognitoError) {
            console.warn(`Failed to delete user ${user.email} from Cognito:`, cognitoError);
            deletionResults.errors.push(`Failed to delete user ${user.email} from Cognito: ${cognitoError}`);
          }
        } catch (error) {
          deletionResults.errors.push(`Failed to delete user ${user.id}: ${error}`);
        }
      }

      // Finally, delete the company
      try {
        await this.client.models.Company.delete({ id: companyId });
        deletionResults.company = companyId;
      } catch (error) {
        deletionResults.errors.push(`Failed to delete company ${companyId}: ${error}`);
      }

      return deletionResults;

    } catch (error) {
      console.error('Error during cascade deletion:', error);
      throw new Error(`Cascade deletion failed: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Delete all data related to an activity
   */
  private async deleteActivityRelatedData(activityId: string): Promise<void> {
    try {
      // Delete pictures
      const pictures = await this.client.models.Picture.list({
        filter: { activityID: { eq: activityId } }
      });
      for (const picture of pictures.data) {
        await this.client.models.Picture.delete({ id: picture.id });
      }

      // Delete amenities
      const amenities = await this.client.models.Amenities.list({
        filter: { activityID: { eq: activityId } }
      });
      for (const amenity of amenities.data) {
        await this.client.models.Amenities.delete({ id: amenity.id });
      }

      // Delete important information
      const importantInfo = await this.client.models.ImportantInformation.list({
        filter: { activityID: { eq: activityId } }
      });
      for (const info of importantInfo.data) {
        await this.client.models.ImportantInformation.delete({ id: info.id });
      }

      // Delete includes
      const includes = await this.client.models.Includes.list({
        filter: { activityID: { eq: activityId } }
      });
      for (const include of includes.data) {
        await this.client.models.Includes.delete({ id: include.id });
      }

      // Delete meeting points
      const meetingPoints = await this.client.models.MeetingPoint.list({
        filter: { activityID: { eq: activityId } }
      });
      for (const meetingPoint of meetingPoints.data) {
        await this.client.models.MeetingPoint.delete({ id: meetingPoint.id });
      }

      // Delete availability (time slots are now stored as JSON string in Availability)
      const availability = await this.client.models.Availability.list({
        filter: { activityID: { eq: activityId } }
      });
      for (const avail of availability.data) {
        // Delete availability
        await this.client.models.Availability.delete({ id: avail.id });
      }

    } catch (error) {
      console.error(`Error deleting activity related data for ${activityId}:`, error);
      throw error;
    }
  }

  /**
   * Search activities by name or description (excluding archived)
   */
  searchActivities(searchTerm: string): Observable<Activity[]> {
    return from(this.client.models.Activity.list(this.publicReadOptions)).pipe(
      map(response => response.data.filter(activity => 
        !activity.archived &&
        (activity.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        activity.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        activity.fullDescription?.toLowerCase().includes(searchTerm.toLowerCase()))
      )),
      catchError(error => throwError(() => new Error(`Failed to search activities: ${error.message}`)))
    );
  }

  /**
   * Get activities by price range
   */
  getActivitiesByPriceRange(minPrice: number, maxPrice: number): Observable<Activity[]> {
    return from(this.client.models.Activity.list(this.publicReadOptions)).pipe(
      map(response => response.data.filter(activity => 
        activity.pricePerPerson && 
        activity.pricePerPerson >= minPrice && 
        activity.pricePerPerson <= maxPrice
      )),
      catchError(error => throwError(() => new Error(`Failed to filter activities by price: ${error.message}`)))
    );
  }

  /**
   * Get activities by rating
   */
  getActivitiesByRating(minRating: number): Observable<Activity[]> {
    return from(this.client.models.Activity.list(this.publicReadOptions)).pipe(
      map(response => response.data.filter(activity => 
        activity.rating && activity.rating >= minRating
      )),
      catchError(error => throwError(() => new Error(`Failed to filter activities by rating: ${error.message}`)))
    );
  }

  // ==================== SITE CONFIGURATION METHODS ====================

  /**
   * List all SiteConfiguration rows (follows nextToken pages).
   */
  private async fetchAllSiteConfigurations(filter?: {
    location: { eq: string };
  }): Promise<SiteConfigurationSchema[]> {
    const aggregated: SiteConfigurationSchema[] = [];
    let nextToken: string | null | undefined;
    do {
      const args: Record<string, unknown> = {};
      if (filter) {
        args.filter = filter;
      }
      if (nextToken) {
        args.nextToken = nextToken;
      }
      const response = await this.client.models.SiteConfiguration.list({
        ...(args as any),
        ...this.publicReadOptions,
      });
      aggregated.push(...(response.data ?? []));
      nextToken = response.nextToken ?? undefined;
    } while (nextToken);
    return aggregated;
  }

  private hydrateSiteConfig(
    config: SiteConfigurationSchema | null
  ): Observable<SiteConfigurationSchema | null> {
    if (!config) {
      return of(null);
    }
    return from(this.storageService.refreshSiteConfigurationUrls(config));
  }

  /**
   * Get all site configurations
   */
  getSiteConfigurations(): Observable<SiteConfigurationSchema[]> {
    return from(this.fetchAllSiteConfigurations()).pipe(
      switchMap((items) => {
        if (items.length === 0) {
          return of([]);
        }
        return forkJoin(items.map((c) => from(this.storageService.refreshSiteConfigurationUrls(c))));
      }),
      catchError(error => throwError(() => new Error(`Failed to fetch site configurations: ${error.message}`)))
    );
  }

  /**
   * Get site configuration by location
   */
  getSiteConfigurationByLocation(location: string): Observable<SiteConfigurationSchema | null> {
    return from(this.fetchAllSiteConfigurations({ location: { eq: location } })).pipe(
      map((list) => list[0] ?? null),
      switchMap((config) => this.hydrateSiteConfig(config)),
      catchError(error => throwError(() => new Error(`Failed to fetch site configuration: ${error.message}`)))
    );
  }

  /**
   * Get active site configuration
   * Note: Amplify doesn't support boolean filters directly, so we filter client-side
   */
  getActiveSiteConfiguration(): Observable<SiteConfigurationSchema | null> {
    return from(this.fetchAllSiteConfigurations()).pipe(
      map((configs) => configs.find((c) => c.active === true) ?? null),
      switchMap((config) => this.hydrateSiteConfig(config)),
      catchError(error => throwError(() => new Error(`Failed to fetch active site configuration: ${error.message}`)))
    );
  }

  /**
   * Get site configuration by ID
   */
  getSiteConfigurationById(id: string): Observable<SiteConfigurationSchema | null> {
    return from(this.client.models.SiteConfiguration.get({ id }, this.publicReadOptions)).pipe(
      map((response) => response.data ?? null),
      switchMap((config) => this.hydrateSiteConfig(config)),
      catchError(error => throwError(() => new Error(`Failed to fetch site configuration: ${error.message}`)))
    );
  }

  /**
   * Create site configuration
   */
  createSiteConfiguration(config: Partial<SiteConfigurationSchema>): Observable<SiteConfigurationSchema> {
    return from(this.client.models.SiteConfiguration.create(config as any)).pipe(
      map(response => response.data as SiteConfigurationSchema),
      catchError(error => throwError(() => new Error(`Failed to create site configuration: ${error.message}`)))
    );
  }

  /**
   * Update site configuration
   */
  updateSiteConfiguration(id: string, config: Partial<SiteConfigurationSchema>): Observable<SiteConfigurationSchema> {
    return from(this.client.models.SiteConfiguration.update({ id, ...(config as any) })).pipe(
      map(response => response.data as SiteConfigurationSchema),
      catchError(error => throwError(() => new Error(`Failed to update site configuration: ${error.message}`)))
    );
  }

  /**
   * Delete site configuration
   */
  deleteSiteConfiguration(id: string): Observable<void> {
    return from(this.client.models.SiteConfiguration.delete({ id })).pipe(
      map(() => void 0),
      catchError(error => throwError(() => new Error(`Failed to delete site configuration: ${error.message}`)))
    );
  }

  /**
   * Set active site configuration (deactivates others)
   */
  setActiveSiteConfiguration(id: string): Observable<void> {
    return this.getSiteConfigurations().pipe(
      switchMap((allConfigs) => {
        // Find all active configurations except the one we're activating
        const activeConfigs = allConfigs.filter(c => c.id && c.active === true && c.id !== id);
        
        // Create observables to deactivate all active configs
        const deactivateObservables = activeConfigs
          .filter(config => config.id)
          .map(config => this.updateSiteConfiguration(config.id!, { active: false } as any));
        
        // If there are configs to deactivate, do that first, then activate the selected one
        if (deactivateObservables.length > 0) {
          return forkJoin(deactivateObservables).pipe(
            switchMap(() => this.updateSiteConfiguration(id, { active: true } as any)),
            map(() => void 0)
          );
        } else {
          // Just activate the selected one
          return this.updateSiteConfiguration(id, { active: true } as any).pipe(
            map(() => void 0)
          );
        }
      }),
      catchError(error => throwError(() => new Error(`Failed to set active site configuration: ${error.message}`)))
    );
  }
}
